const CROPPER_IMAGE_RETRY_ATTEMPTS = 40;
const CROPPER_IMAGE_RETRY_DELAY_MS = 500;

/**
 * The origin media URL can return 500 until processing finishes. Retry until the
 * image decodes, then return a blob URL the cropper can show. Returns null if it
 * never becomes available.
 */
export async function waitForCropperImage(pictureUrl: string): Promise<string | null> {
  if (!pictureUrl) {
    return null;
  }

  for (let attempt = 0; attempt < CROPPER_IMAGE_RETRY_ATTEMPTS; attempt += 1) {
    const attemptUrl = withCacheBuster(pictureUrl, attempt);
    const objectUrl = await tryCreateImageObjectUrl(attemptUrl);
    if (objectUrl) {
      return objectUrl;
    }

    await delay(CROPPER_IMAGE_RETRY_DELAY_MS);
  }

  return null;
}

/**
 * Release a blob URL created for the cropper. Call this when a newer image replaces
 * it, and again when the owner is destroyed, so the decoded image is not kept in memory.
 */
export function revokeCropperObjectUrl(objectUrl: string | null): void {
  if (!objectUrl) {
    return;
  }

  URL.revokeObjectURL(objectUrl);
}

/**
 * Fetch one attempt of the media URL and return a blob URL only when the response
 * is a decodable image. Returns null on a 500 or any other response the cropper cannot show.
 */
async function tryCreateImageObjectUrl(pictureUrl: string): Promise<string | null> {
  try {
    const response = await fetch(pictureUrl, { cache: 'no-store', mode: 'cors' });
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    if (!blob.size) {
      return null;
    }

    const contentType = (response.headers.get('content-type') ?? blob.type).split(';')[0].trim();
    if (contentType && !contentType.startsWith('image/') && contentType !== 'application/octet-stream') {
      return null;
    }

    const objectUrl = URL.createObjectURL(blob);
    const canDecode = await canDecodeImage(objectUrl);
    if (!canDecode) {
      URL.revokeObjectURL(objectUrl);
      return null;
    }

    return objectUrl;
  } catch {
    return null;
  }
}

function canDecodeImage(objectUrl: string): Promise<boolean> {
  return new Promise(resolve => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = objectUrl;
  });
}

function withCacheBuster(pictureUrl: string, attempt: number): string {
  try {
    const url = new URL(pictureUrl);
    url.searchParams.set('t', `${Date.now()}-${attempt}`);
    return url.toString();
  } catch {
    const separator = pictureUrl.includes('?') ? '&' : '?';
    return `${pictureUrl}${separator}t=${Date.now()}-${attempt}`;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}
